import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  endpoints,
  numericToNumber,
  type Channel,
  type ChannelScore,
  type Evaluation,
  type Video,
} from "@/lib/api";
import { fmtDate, fmtNumber, fmtPercent, fmtRank, fmtTimestamp, platformLabel } from "@/lib/format";
import { Badge, decisionVariant, lifecycleVariant, lifecycleLabel } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";

// ChannelDrawer — right-side preview pane that opens when a row in the
// channels list is clicked. Shows the channel header + score + recent
// content + recent evaluations. Lighter than the full /channels/:id page;
// the "查看完整页 →" link goes there.
//
// v2.4.8 — extracted from Channels.tsx (was 1123 LOC).
export function ChannelDrawer({
  channelID,
  onClose,
}: {
  channelID: number;
  onClose: () => void;
}) {
  const channelQ = useQuery({
    queryKey: ["channel", channelID],
    queryFn: () => endpoints.channel(channelID),
  });
  const scoreQ = useQuery({
    queryKey: ["channel-score", channelID],
    queryFn: () => endpoints.channelScore(channelID),
    retry: false,
  });
  const videosQ = useQuery({
    queryKey: ["channel-videos", channelID],
    queryFn: () => endpoints.channelVideos(channelID, 8),
  });
  const evalQ = useQuery({
    queryKey: ["channel-evals", channelID],
    queryFn: () => endpoints.channelEvaluations(channelID, 5),
  });

  const channel = channelQ.data;

  return (
    <aside className="w-[28rem] shrink-0 overflow-y-auto border-l border-border bg-background">
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background px-4 py-3">
        <h2 className="text-sm font-semibold">频道详情</h2>
        <div className="flex items-center gap-1">
          <Link
            to={`/channels/${channelID}`}
            className="rounded-md px-2 py-1 text-xs text-primary hover:bg-muted"
          >
            查看完整页 →
          </Link>
          <Button variant="ghost" size="sm" onClick={onClose}>
            关闭
          </Button>
        </div>
      </div>
      <div className="p-4 space-y-5">
        {channelQ.isLoading && <p className="text-sm text-muted-foreground">加载中…</p>}
        {channel && <DrawerHeader channel={channel} />}
        <DrawerScore data={scoreQ.data} loading={scoreQ.isLoading} />
        <DrawerVideos data={videosQ.data?.data ?? []} loading={videosQ.isLoading} />
        <DrawerEvals data={evalQ.data?.data ?? []} loading={evalQ.isLoading} />
      </div>
    </aside>
  );
}

function DrawerHeader({ channel }: { channel: Channel }) {
  return (
    <div>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold">
            {channel.Name ?? channel.Handle ?? channel.PlatformChannelID}
          </h3>
          {channel.Url && (
            <a
              href={channel.Url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-primary hover:underline break-all"
            >
              {channel.Url}
            </a>
          )}
        </div>
        <Badge variant={lifecycleVariant(channel.LifecycleStatus)}>
          {lifecycleLabel(channel.LifecycleStatus)}
        </Badge>
      </div>
      {channel.Description && (
        <p className="mt-2 line-clamp-3 text-xs text-muted-foreground">
          {channel.Description}
        </p>
      )}
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <DT label="平台">{platformLabel(channel.Platform)}</DT>
        <DT label="跟踪">{channel.TrackingMode}</DT>
        <DT label="粉丝">{fmtNumber(channel.FollowerCount)}</DT>
        <DT label="内容数">{fmtNumber(channel.TotalContentCount)}</DT>
        <DT label="区域">{channel.Region ?? "—"}</DT>
        <DT label="语言">{channel.Language ?? "—"}</DT>
        <DT label="内容类型">{channel.ContentType ?? "—"}</DT>
        <DT label="内部分级">{channel.InternalTier ?? "—"}</DT>
        <DT label="最后内容">{fmtTimestamp(channel.LastContentAt)}</DT>
        <DT label="最近抓取">{fmtTimestamp(channel.LastFetchedAt)}</DT>
      </dl>
    </div>
  );
}

function DT({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function DrawerScore({
  data,
  loading,
}: {
  data: ChannelScore | undefined;
  loading: boolean;
}) {
  if (loading) return <Section title="评分快照"><p className="text-xs text-muted-foreground">加载中…</p></Section>;
  if (!data) {
    return (
      <Section title="评分快照">
        <p className="text-xs text-muted-foreground">尚未计算分数</p>
      </Section>
    );
  }
  const eng = numericToNumber(data.EngagementRate);
  const growth = numericToNumber(data.GrowthRate90d);
  return (
    <Section title="评分快照">
      <div className="grid grid-cols-2 gap-2 text-xs">
        <MetricRow label="粉丝" value={fmtNumber(data.FollowerCount)} rank={fmtRank(numericToNumber(data.FollowerPct))} />
        <MetricRow label="30 日活跃" value={fmtNumber(data.Activity30d)} rank={fmtRank(numericToNumber(data.ActivityPct))} />
        <MetricRow label="互动率 (30d)" value={fmtPercent(eng, 2)} rank={fmtRank(numericToNumber(data.EngagementPct))} />
        <MetricRow label="90 日增长" value={fmtPercent(growth, 1)} rank={fmtRank(numericToNumber(data.GrowthPct))} />
        <MetricRow label="竞品重叠 (90d)" value={fmtNumber(data.CompetitorOverlap)} rank="—" />
      </div>
      {data.AutoDecisionSuggestion && (
        <Badge className="mt-2" variant={decisionVariant(data.AutoDecisionSuggestion)}>
          自动建议：{data.AutoDecisionSuggestion}
        </Badge>
      )}
      <p className="mt-1 text-[10px] text-muted-foreground">
        计算于 {fmtTimestamp(data.ComputedAt)}
      </p>
    </Section>
  );
}

function MetricRow({ label, value, rank }: { label: string; value: string; rank: string }) {
  return (
    <div className="rounded border border-border bg-muted/30 px-2 py-1">
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className="font-semibold">{value}</div>
      <div className="text-[10px] text-muted-foreground">{rank}</div>
    </div>
  );
}

function DrawerVideos({
  data,
  loading,
}: {
  data: Video[];
  loading: boolean;
}) {
  if (loading) return <Section title="最近内容"><p className="text-xs text-muted-foreground">加载中…</p></Section>;
  if (data.length === 0) return <Section title="最近内容"><p className="text-xs text-muted-foreground">暂无</p></Section>;
  return (
    <Section title="最近内容">
      <ul className="space-y-2">
        {data.map((v) => (
          <li key={v.ID} className="text-xs">
            <div className="font-medium line-clamp-1">{v.Title ?? v.PlatformVideoID}</div>
            <div className="text-muted-foreground">
              {fmtDate(v.PublishedAt)} · 👁 {fmtNumber(v.ViewCount)} · 👍 {fmtNumber(v.LikeCount)}
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function DrawerEvals({
  data,
  loading,
}: {
  data: Evaluation[];
  loading: boolean;
}) {
  if (loading) return <Section title="决策历史"><p className="text-xs text-muted-foreground">加载中…</p></Section>;
  if (data.length === 0) return <Section title="决策历史"><p className="text-xs text-muted-foreground">暂无</p></Section>;
  return (
    <Section title="决策历史">
      <ul className="space-y-2">
        {data.map((e) => (
          <li key={e.ID} className="rounded border border-border bg-muted/20 px-2 py-1.5 text-xs">
            <div className="flex items-center gap-2">
              <Badge variant={decisionVariant(e.Decision)}>{e.Decision}</Badge>
              <span className="text-muted-foreground">{fmtDate(e.EvaluatedAt)}</span>
              {e.Evaluator && <span className="ml-auto text-muted-foreground">by {e.Evaluator}</span>}
            </div>
            {e.Rationale && <p className="mt-1 line-clamp-2 text-muted-foreground">{e.Rationale}</p>}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h4>
      {children}
    </div>
  );
}
